import { useState } from "react";
import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
} from "./shared";

interface FAQAccordionBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function FAQAccordionBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: FAQAccordionBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const eyebrow = get("eyebrow", "");
  const heading = get("heading", "");
  const subtext = get("subtext", "");
  const items = get("items", []) as { id: string; q: string; a: string }[];
  const stats = get("stats", []) as { value: string; label: string }[];

  const setFAQItem = (idx: number, key: "q" | "a", val: string) => {
    const newItems = items.map((item, i) => (i === idx ? { ...item, [key]: val } : item));
    setConfig("items", newItems);
  };

  const deleteFAQItem = (idx: number) => {
    setConfig("items", items.filter((_, i) => i !== idx));
  };

  const addFAQItem = () => {
    setConfig("items", [...items, { id: `faq-new-${Date.now()}`, q: "New Question", a: "Answer goes here..." }]);
  };

  const setFAQStat = (idx: number, key: "value" | "label", val: string) => {
    const newStats = stats.map((s, i) => (i === idx ? { ...s, [key]: val } : s));
    setConfig("stats", newStats);
  };

  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const cardBase = isDark ? "border-gray-700 bg-gray-800" : "border-gray-100 bg-white";
  const cardActive = isDark ? "border-pink-500 bg-gray-700" : "border-pink-300 bg-pink-50";

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="faq-accordion"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        <div className="flex flex-col lg:flex-row gap-10 items-start">
          {/* Left panel */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div
              className="rounded-3xl p-8 flex flex-col items-center justify-center text-center h-56"
              style={{ background: "linear-gradient(135deg, #f9a8d4 0%, #86efac 100%)" }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-white/30 rounded-full mb-3">
                <i className="ri-question-line text-white text-3xl"></i>
              </div>
              <p className="text-white text-xl font-bold" style={{ fontFamily: headingFont, ...headingStyle }}>
                Got Questions?
              </p>
              <p className="text-white/80 text-sm mt-1">We&apos;ve got answers below</p>
            </div>
            <div className="mt-6">
              {eyebrow && (
                <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{eyebrow}</span>
              )}
              {heading && (
                <h2 className={`text-2xl md:text-3xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                  {heading}
                </h2>
              )}
              {subtext && <p className={`text-sm mb-5 ${subColor}`}>{subtext}</p>}
              {stats.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {stats.map((s, i) => (
                    <div key={i} className={`rounded-xl p-3 text-center ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
                      {isAdmin && onUpdate ? (
                        <>
                          <AdminEditField
                            value={s.value}
                            onSave={(v) => setFAQStat(i, "value", v)}
                            inputClassName="text-lg font-extrabold text-pink-500"
                          >
                            <div className="text-lg font-extrabold text-pink-500">{s.value}</div>
                          </AdminEditField>
                          <AdminEditField
                            value={s.label}
                            onSave={(v) => setFAQStat(i, "label", v)}
                            inputClassName={`text-xs mt-0.5 ${subColor}`}
                          >
                            <div className={`text-xs mt-0.5 ${subColor}`}>{s.label}</div>
                          </AdminEditField>
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-extrabold text-pink-500">{s.value}</div>
                          <div className={`text-xs mt-0.5 ${subColor}`}>{s.label}</div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right panel — FAQ items */}
          <div className="w-full lg:w-3/5 space-y-1">
            {isAdmin && onUpdate && (
              <button
                onClick={(e) => { e.stopPropagation(); addFAQItem(); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-green-600 border border-dashed border-green-400 rounded-xl py-2 mb-2 cursor-pointer hover:bg-green-50 transition-colors"
              >
                <i className="ri-add-line"></i> Add FAQ Item
              </button>
            )}
            {items.map((item, i) => (
              <div
                key={item.id}
                className={`rounded-xl border transition-all relative group/faqitem ${openIndex === i ? cardActive : cardBase}`}
              >
                {isAdmin && onUpdate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteFAQItem(i); }}
                    className="absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/faqitem:opacity-100"
                    title="Delete FAQ item"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                )}
                <button
                  className="w-full flex items-center justify-between px-4 py-4 text-left cursor-pointer"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  {isAdmin && onUpdate ? (
                    <AdminEditField
                      value={item.q}
                      onSave={(v) => setFAQItem(i, "q", v)}
                      inputClassName={`font-bold text-sm pr-4 ${headingColor}`}
                    >
                      <span className={`font-bold text-sm pr-4 ${headingColor}`}>{item.q}</span>
                    </AdminEditField>
                  ) : (
                    <span className={`font-bold text-sm pr-4 ${headingColor}`}>{item.q}</span>
                  )}
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                      openIndex === i ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <i className={openIndex === i ? "ri-subtract-line text-xs" : "ri-add-line text-xs"}></i>
                  </span>
                </button>
                {openIndex === i && (
                  <div className={`px-5 pb-4 text-sm leading-relaxed ${subColor}`}>
                    {isAdmin && onUpdate ? (
                      <AdminEditField
                        value={item.a}
                        onSave={(v) => setFAQItem(i, "a", v)}
                        multiline
                        inputClassName="text-sm leading-relaxed"
                      >
                        {item.a}
                      </AdminEditField>
                    ) : (
                      item.a
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}